import { forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';

interface NumericStepperProps {
  value: number | null | undefined;
  onChange: (value: number) => void;
  onBlur?: () => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  prefixLabel?: string;
  suffixLabel?: string;
  id?: string;
  name?: string;
  step?: number;
}

export const NumericStepper = forwardRef<HTMLInputElement, NumericStepperProps>(
  function NumericStepper(
    { value, onChange, onBlur, disabled, min, max, prefixLabel, suffixLabel, id, name, step = 1 },
    ref,
  ) {
    const clamp = (n: number) => {
      if (typeof max === 'number' && n > max) return max;
      if (typeof min === 'number' && n < min) return min;
      return n;
    };
    const current = typeof value === 'number' ? value : 0;
    const stepBtn =
      'inline-flex items-center justify-center w-10 shrink-0 bg-white text-hilevel-slate hover:bg-hilevel-light focus:outline-none focus:ring-2 focus:ring-inset focus:ring-hilevel-red/30 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer border-0';
    const affix = 'inline-flex items-center px-2.5 text-sm text-hilevel-gray bg-hilevel-light';
    return (
      <fieldset disabled={disabled} className="m-0 p-0 border-0">
        <div className="flex h-10 w-full overflow-hidden rounded-md border border-hilevel-border focus-within:ring-2 focus-within:ring-hilevel-red/30 focus-within:border-hilevel-red transition">
          <button
            type="button"
            className={`${stepBtn} border-r border-hilevel-border`}
            onClick={() => onChange(clamp(current - step))}
            disabled={disabled}
            aria-label="Decrease"
          >
            <FontAwesomeIcon icon={faMinus} className="text-xs" aria-hidden="true" />
          </button>
          {prefixLabel ? (
            <span className={`${affix} border-r border-hilevel-border`}>{prefixLabel}</span>
          ) : null}
          <input
            id={id}
            name={name}
            ref={ref}
            type="number"
            className="min-w-0 flex-1 bg-white px-3 text-sm text-hilevel-slate text-center focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            value={value ?? ''}
            min={min}
            max={max}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => {
              const v = e.target.value;
              onChange(v === '' ? (NaN as unknown as number) : Number(v));
            }}
            onBlur={onBlur}
          />
          {suffixLabel ? (
            <span className={`${affix} border-l border-hilevel-border`}>{suffixLabel}</span>
          ) : null}
          <button
            type="button"
            className={`${stepBtn} border-l border-hilevel-border`}
            onClick={() => onChange(clamp(current + step))}
            disabled={disabled}
            aria-label="Increase"
          >
            <FontAwesomeIcon icon={faPlus} className="text-xs" aria-hidden="true" />
          </button>
        </div>
      </fieldset>
    );
  },
);
